"use client";

import React from "react";
import * as RadioGroupPrimitive from "@radix-ui/react-radio-group";
import { VariantProps, tv } from "tailwind-variants";

const radioGroupVariant = tv({
  slots: {
    root: "flex flex-row gap-2",
    itemWrapper: "flex items-center gap-2",
    item: [
      "flex aspect-square shrink-0 cursor-pointer items-center justify-center rounded-full outline-none",
      "border-neutral-400 bg-white",
      "[&:not(:disabled)]:data-[state=checked]:border-secondary-600",
      "hover:border-secondary-500 hover:bg-secondary-200 focus:border-secondary-600",
    ],
    label: "cursor-pointer text-body-1-300 text-neutral-600",
    indicator: [
      "flex h-full w-full items-center justify-center rounded-full text-white",
      "[&:not(:data-[state=checked])]:hover:bg-secondary-100 [&:not(:disabled)]:bg-secondary-600",
    ],
  },
  variants: {
    size: {
      lg: {
        item: "h-6 w-6 border-2 p-[1.75px] focus:border-2",
      },
      sm: {
        item: "h-4 w-4 border p-[0.8px] focus:border",
      },
    },
    disabled: {
      true: {
        item: "cursor-not-allowed data-[disabled]:!border-neutral-300 data-[disabled]:!bg-neutral-200",
        label: "cursor-not-allowed text-neutral-400",
        indicator: "hidden",
      },
      false: {
        item: "",
        label: "",
        indicator: "",
      },
    },
    orientation: {
      vertical: {
        root: "flex flex-col",
      },
      horizontal: {
        root: "flex flex-row",
      },
    },
  },
  defaultVariants: {
    size: "lg",
    orientation: "vertical",
  },
});

type RadioGroupVariant = VariantProps<typeof radioGroupVariant>;

type RadioItemProps = React.ComponentProps<typeof RadioGroupPrimitive.Item> & {
  id: string;
  value: string;
  label?: string;
};

export interface RadioProps
  extends React.ComponentProps<typeof RadioGroupPrimitive.Root>,
    RadioGroupVariant {
  items: RadioItemProps[];
  itemWrapperClassName?: string;
  itemClassName?: string;
  labelClassName?: string;
  onValueChange?: (value: string) => void;
}

const Radio: React.FC<RadioProps> = ({
  className,
  value,
  defaultValue,
  size,
  disabled,
  orientation,
  itemWrapperClassName,
  itemClassName,
  labelClassName,
  items,
  onValueChange,
  ...props
}) => {
  const { root, itemWrapper, item, label, indicator } = radioGroupVariant({
    size,
    disabled,
    orientation,
  });

  return (
    <RadioGroupPrimitive.Root
      value={value}
      defaultValue={defaultValue}
      className={root({ class: className })}
      onValueChange={onValueChange}
      disabled={disabled}
      {...props}
    >
      {items.map((radioItem, index) => (
        <div
          key={`${radioItem.value}-${index}`}
          className={itemWrapper({ class: itemWrapperClassName })}
        >
          <RadioGroupPrimitive.Item
            id={radioItem.id}
            value={radioItem.value}
            disabled={disabled}
            className={item({ class: itemClassName })}
          >
            <RadioGroupPrimitive.Indicator className={indicator()} />
          </RadioGroupPrimitive.Item>
          {radioItem.label && (
            <label
              className={label({ class: labelClassName })}
              htmlFor={radioItem.id}
            >
              {radioItem.label}
            </label>
          )}
        </div>
      ))}
    </RadioGroupPrimitive.Root>
  );
};

export default Radio;
